import { oc } from "@orpc/contract";
import { z } from "zod";

const MetadataSchema = z.record(z.string(), z.unknown()).nullable();

export const LeaseableResourceSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  resourceType: z.string(),
  resourceId: z.uuid(),
  maxConcurrent: z.number(),
  leaseTtlSeconds: z.number(),
  isActive: z.boolean(),
  metadata: MetadataSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ResourceLeaseSchema = z.object({
  id: z.uuid(),
  resourceId: z.uuid(),
  idempotencyKey: z.string(),
  lesseeId: z.uuid(),
  status: z.enum(["active", "returned", "expired"]),
  leasedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  returnedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const leaseableResourceContracts = {
  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        resourceType: z.string().min(1).max(100),
        resourceId: z.uuid(),
        maxConcurrent: z.number().int().min(1).optional(),
        leaseTtlSeconds: z.number().int().min(1).optional(),
        isActive: z.boolean().optional(),
        metadata: MetadataSchema.optional(),
      }),
    )
    .output(LeaseableResourceSchema)
    .route({ method: "POST" }),
  get: oc
    .input(z.object({ resourceId: z.uuid() }))
    .output(LeaseableResourceSchema.nullable())
    .route({ method: "GET" }),
  list: oc
    .input(
      z.object({
        resourceType: z.string().optional(),
        isActive: z.boolean().optional(),
        limit: z.number().int().min(1).max(100).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({
        resources: z.array(LeaseableResourceSchema),
        total: z.number(),
      }),
    )
    .route({ method: "GET" }),
  update: oc
    .input(
      z.object({
        resourceId: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        maxConcurrent: z.number().int().min(1).optional(),
        leaseTtlSeconds: z.number().int().min(1).optional(),
        isActive: z.boolean().optional(),
        metadata: MetadataSchema.optional(),
      }),
    )
    .output(LeaseableResourceSchema.nullable())
    .route({ method: "PUT" }),
  delete: oc
    .input(z.object({ resourceId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),
  linkAgent: oc
    .input(
      z.object({
        agentDefinitionId: z.uuid(),
        resourceId: z.uuid(),
        isRequired: z.boolean().optional(),
        sortOrder: z.number().int().optional(),
      }),
    )
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),
  unlinkAgent: oc
    .input(z.object({ agentDefinitionId: z.uuid(), resourceId: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),
  listForAgent: oc
    .input(z.object({ agentDefinitionId: z.uuid() }))
    .output(
      z.object({
        resources: z.array(
          LeaseableResourceSchema.extend({
            isRequired: z.boolean(),
            sortOrder: z.number(),
          }),
        ),
      }),
    )
    .route({ method: "GET" }),
  listLeases: oc
    .input(
      z.object({
        resourceId: z.uuid(),
        limit: z.number().int().min(1).max(100).default(50),
        offset: z.number().int().min(0).default(0),
      }),
    )
    .output(
      z.object({ leases: z.array(ResourceLeaseSchema), total: z.number() }),
    )
    .route({ method: "GET" }),
} as const;
